import {
  RawFlight,
  Vuelos,
  VuelosFinalResponse,
} from "@/lib/api/vuelos/types";

const API_URL = process.env.NEXT_PUBLIC_VUELOS_API_URL!;
const API_KEY = process.env.NEXT_PUBLIC_VUELOS_API_KEY || "";

export type AirportCode = "pty" | "blb" | "dav" | "rih" | "all";
export type FlightType = "arrival" | "departure" | "all";
export type DayFilter = "hoy" | "mañana";
export type Tipo = "arrivals" | "departures";

export interface FetchVuelosParams {
  airport?: AirportCode | string;
  type?: FlightType;
  date?: string;
  day?: DayFilter;
  id?: number | string;
  flight?: string;
  flight_number?: string;
  airline?: string;
  origin?: string;
  destination?: string;
  route?: string;
  terminal?: string;
}

// YYYY-MM-DD en zona Panamá (UTC-5, sin DST)
function panamaDate(offsetDays = 0): string {
  const now = new Date();
  const panama = new Date(now.getTime() - 5 * 60 * 60 * 1000);
  panama.setUTCDate(panama.getUTCDate() + offsetDays);
  return panama.toISOString().slice(0, 10);
}

function tipoToType(tipo: Tipo): "arrival" | "departure" {
  return tipo === "arrivals" ? "arrival" : "departure";
}

function typeToTipo(t: "arrival" | "departure"): Tipo {
  return t === "arrival" ? "arrivals" : "departures";
}

function mapRawFlight(raw: RawFlight): Vuelos {
  const isArrival = raw.type === "arrival";
  return {
    id: raw.id ?? 0,
    routing: isArrival ? raw.origin_code : raw.destination_code,
    routingDescEs: isArrival ? raw.origin : raw.destination,
    horaEstimada: raw.scheduled?.datetime ?? "",
    horaReal: raw.estimated?.datetime ?? "",
    horaLlegadaOficial: "",
    horaSalidaOficial: "",
    terminal: raw.terminal ?? "",
    vuelo: raw.airline_code ?? "",
    vueloNum: raw.flight_number ?? "",
    aerolinea: raw.airline ?? "",
    puerta: raw.gate ?? "",
    equipaje: raw.belt || "N/A",
    estado: raw.status || raw.status_public || raw.status_internal || "",
    tipo: typeToTipo(raw.type),
    codeshare: raw.codeshare,
  };
}

function buildSearchParams(params: FetchVuelosParams): URLSearchParams {
  const sp = new URLSearchParams();

  if (params.airport) {
    sp.set("airport", params.airport);
    if (params.airport.toLowerCase() !== "pty") {
      sp.set("service_type", "all");
    }
  }
  if (params.type && params.type !== "all") sp.set("type", params.type);

  if (params.date) {
    sp.set("date", params.date);
  } else if (params.day) {
    sp.set("date", panamaDate(params.day === "hoy" ? 0 : 1));
  }

  if (params.id !== undefined && params.id !== null && params.id !== "") {
    sp.set("id", String(params.id));
  }
  if (params.flight) sp.set("flight", params.flight);
  if (params.flight_number) sp.set("flight_number", params.flight_number);
  if (params.airline) sp.set("airline", params.airline);
  if (params.origin) sp.set("origin", params.origin);
  if (params.destination) sp.set("destination", params.destination);
  if (params.route) sp.set("route", params.route);
  if (params.terminal) sp.set("terminal", params.terminal);

  return sp;
}

async function callVuelosFinal(
  params: FetchVuelosParams,
): Promise<RawFlight[]> {
  const sp = buildSearchParams(params);
  const query = sp.toString();
  const url = `${API_URL}vuelosfinal${query ? `?${query}` : ""}`;

  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(API_KEY ? { "x-api-key": API_KEY } : {}),
    },
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error(`Error al obtener vuelos: ${res.status}`);
  }

  const json = (await res.json()) as VuelosFinalResponse;

  if (json.status !== "success" || !json.data) {
    throw new Error(json.message || "Respuesta inválida del API de vuelos");
  }

  return json.data.flights ?? [];
}

/**
 * Núcleo del fetch — admite todos los filtros del nuevo endpoint /vuelosfinal.
 */
export async function fetchVuelosFinal(
  params: FetchVuelosParams = {},
): Promise<Vuelos[]> {
  const flights = await callVuelosFinal(params);
  return flights.map(mapRawFlight);
}

/**
 * Wrapper de compatibilidad: lista de vuelos por tipo, día y aeropuerto.
 */
export async function fetchVuelos(
  tipo: Tipo,
  dia: DayFilter,
  airport: AirportCode = "pty",
): Promise<Vuelos[]> {
  return fetchVuelosFinal({
    airport,
    type: tipoToType(tipo),
    day: dia,
  });
}

/**
 * Wrapper de compatibilidad: vuelo único por ID.
 *
 * El backend de /vuelosfinal a veces filtra por fecha implícita ("hoy") cuando
 * se consulta solo por ID, lo que hace que vuelos de mañana retornen vacío.
 * Probamos en cascada con varios filtros para garantizar que encontramos el
 * vuelo si existe.
 */
export async function fetchVueloById(
  id: number,
  airport: AirportCode = "all",
): Promise<Vuelos | null> {
  const attempts: FetchVuelosParams[] = [
    { id, airport },
    { id, airport, day: "hoy" },
    { id, airport, day: "mañana" },
    { id, day: "hoy" },
    { id, day: "mañana" },
    { id },
  ];

  for (const params of attempts) {
    try {
      const flights = await callVuelosFinal(params);
      if (flights.length > 0) {
        return mapRawFlight(flights[0]);
      }
    } catch (err) {
      // No abortamos: si un attempt falla, probamos el siguiente
      console.warn(
        `[fetchVueloById] intento falló para ${JSON.stringify(params)}:`,
        err,
      );
    }
  }

  console.warn(
    `[fetchVueloById] vuelo id=${id} no encontrado en ningún filtro`,
  );
  return null;
}
